/**
 * Mixer.js — bus topology, dynamics and the hearing-damage chain.
 *
 *   voices ─┬─> weapons ─┐
 *           ├─> world  ──┤
 *           ├─> player ──┼─> master ─> muffle(LP) ─> compressor ─> limiter ─> clipper ─> out
 *           ├─> ui     ──┤                                ^
 *           └─> music  ──┘                                │
 *   voice sends ─────────> Reverb ──> reverbReturn ───────┘
 *
 * Dynamics: a slow leveller plus a sample-accurate ceiling
 * ---------------------------------------------------------
 * The obvious build — fast compressor into a brickwall limiter — was measured and
 * rejected. Chrome's DynamicsCompressorNode costs roughly 4.5 dB on a sub-millisecond
 * transient *regardless of threshold*: feeding it a signal 45 dB below the threshold
 * with ratio 1:1 still loses 4.5 dB, and the loss tracks the attack time, not the
 * level. A fast-attack node anywhere in the master path therefore flattens every
 * gunshot's crack by more than the crack is worth — which is exactly the processing
 * that makes game audio sound like a phone speaker.
 *
 * So:
 *   - the compressor is set to a 250 ms attack and acts purely as a slow leveller,
 *     riding the sustained loudness of a firefight. At that attack it measures a
 *     0.3 dB cost on a transient, which is free.
 *   - peak safety is the tanh clipper's job instead. It is sample-accurate, has no
 *     detector and therefore no attack time, is exactly unity below its knee, and
 *     asymptotes below full scale — so the output *cannot* clip, and nothing pays for
 *     that guarantee until it is actually needed.
 *
 * Ducking: under the player's own gunfire the world bus drops ~4 dB for a quarter
 * second. Real mixers do this because the ear's own gain control does it too.
 */

import { gain, biquad, softClipper, chain, clamp } from './synth/dsp.js';

export const BUS_NAMES = ['weapons', 'world', 'player', 'ui', 'music'];

export const BUS_DEFAULTS = {
  weapons: 1.0,
  world: 0.9,
  player: 0.95,
  ui: 0.65,
  music: 0.5,
};

export class Mixer {
  constructor(ac, { master = 0.9, buses = BUS_DEFAULTS } = {}) {
    this.ac = ac;

    this.out = gain(ac, 1);
    // Saturation is asymmetric whenever the signal is, and asymmetry means DC. A
    // 20 Hz blocker after the clipper costs nothing and guarantees the output has no
    // offset eating into headroom.
    this.dcBlock = biquad(ac, 'highpass', 20, 0.6);
    this.clipper = softClipper(ac);

    // Slow leveller. The long attack is the entire point: it must not react to any
    // individual transient, only to the sustained level of an ongoing firefight.
    this.compressor = ac.createDynamicsCompressor();
    this.compressor.threshold.value = -8;
    this.compressor.knee.value = 6;
    this.compressor.ratio.value = 4;
    this.compressor.attack.value = 0.25;
    this.compressor.release.value = 0.45;

    // Hearing-damage filter. Sits before the dynamics so a muffled mix also loses the
    // energy that was driving the compressor, exactly as a real ear-protection
    // response would.
    this.muffle = biquad(ac, 'lowpass', 20000, 0.7);
    // Separate from `master` so the per-frame hearing-damage automation and the
    // player's own volume setting never fight over the same AudioParam.
    this.hearing = gain(ac, 1);
    this.master = gain(ac, master);

    chain(this.master, this.hearing, this.muffle, this.compressor, this.clipper, this.dcBlock, this.out);
    this.out.connect(ac.destination);

    this.buses = {};
    this.baseGain = {};
    for (const name of BUS_NAMES) {
      const g = gain(ac, buses[name] ?? 1);
      g.connect(this.master);
      this.buses[name] = g;
      this.baseGain[name] = g.gain.value;
    }

    // The reverb return joins post-bus so a duck on the world bus also ducks that
    // bus's contribution to the room, rather than leaving a naked wet signal.
    this.reverbReturn = gain(ac, 1);
    this.reverbReturn.connect(this.master);

    this._duckUntil = 0;
  }

  bus(name) {
    return this.buses[name] ?? this.buses.world;
  }

  connectReverb(reverb) {
    reverb.output.connect(this.reverbReturn);
    this.reverb = reverb;
    return this;
  }

  setMaster(v, time = 0.05) {
    const now = this.ac.currentTime;
    const p = this.master.gain;
    holdParam(p, now);
    p.linearRampToValueAtTime(clamp(v, 0, 4), now + time);
  }

  setBus(name, v, time = 0.05) {
    const g = this.buses[name];
    if (!g) return;
    this.baseGain[name] = clamp(v, 0, 4);
    const now = this.ac.currentTime;
    holdParam(g.gain, now);
    g.gain.linearRampToValueAtTime(this.baseGain[name], now + time);
  }

  /**
   * Duck a bus to `amount` of its base gain, then recover.
   * Overlapping ducks extend rather than restart, so sustained automatic fire keeps
   * the world bus down instead of pumping it once per shot.
   */
  duck(name, amount = 0.55, attack = 0.02, holdTime = 0.09, release = 0.25) {
    const g = this.buses[name];
    if (!g) return;
    const now = this.ac.currentTime;
    const base = this.baseGain[name];
    const end = now + attack + holdTime + release;
    if (end < this._duckUntil) return;
    this._duckUntil = end;
    const p = g.gain;
    holdParam(p, now);
    p.linearRampToValueAtTime(base * amount, now + attack);
    p.setValueAtTime(base * amount, now + attack + holdTime);
    p.linearRampToValueAtTime(base, end);
  }

  /** Current gain-reduction of the master leveller in dB (negative). Diagnostics. */
  get reductionDb() {
    return this.compressor.reduction ?? 0;
  }

  dispose() {
    try {
      this.out.disconnect();
      this.master.disconnect();
      for (const name of BUS_NAMES) this.buses[name].disconnect();
      this.reverbReturn.disconnect();
    } catch { /* context already closed */ }
  }
}

/** cancelAndHoldAtTime with a fallback for engines that lack it. */
export function holdParam(param, now) {
  try {
    param.cancelAndHoldAtTime(now);
  } catch {
    const v = param.value;
    param.cancelScheduledValues(now);
    param.setValueAtTime(v, now);
  }
}
